/**
 * <daddl-pack-xray> — X-ray reveal over a pack (snippets/daddl-pack-xray).
 *
 * Two modes, picked by `(hover: hover) and (pointer: fine)` and swapped live
 * if that changes (e.g. a tablet gains a trackpad):
 *
 * Lens (mouse / trackpad):
 *   A round lens follows the cursor and closes on leave. Position and radius
 *   ease toward a target each frame so it trails like glass. playIntro()
 *   wanders it across the bag once on first view and when a showcase slide
 *   is picked. Keyboard: focus opens it centred, arrows move, Escape closes.
 *
 * Scroll (touch):
 *   Top → bottom reveal tied to scroll. CSS does it with a scroll timeline
 *   on the marker (transform-only), so normally no script runs. Browsers
 *   without `animation-timeline: view()` get a fallback that writes one
 *   --xray-p per frame, only while the bag is on screen.
 *
 * Always: --xray-bleed (px) follows the frame width (ResizeObserver).
 */
if (!customElements.get('daddl-pack-xray')) {
  const LENS = 0.24; // lens radius, fraction of frame width
  const BLEED = 0.26; // layer bleed past the frame; must be >= LENS
  const EASE = 0.2; // lens easing per frame
  const STEP = 0.05; // arrow-key lens step
  // Fallback scroll range, as fractions of the viewport height where the
  // marker (bag centre) sits. Must match `animation-range: cover 40% cover 80%`.
  const SCROLL_FROM = 0.6;
  const SCROLL_TO = 0.2;

  const LENS_QUERY = window.matchMedia('(hover: hover) and (pointer: fine)');
  const REDUCED_QUERY = window.matchMedia('(prefers-reduced-motion: reduce)');
  const NATIVE_SCROLL = window.CSS && CSS.supports('animation-timeline: view()');

  const clamp01 = (n) => Math.min(1, Math.max(0, n));
  const smooth = (t) => t * t * (3 - 2 * t);

  customElements.define(
    'daddl-pack-xray',
    class DaddlPackXray extends HTMLElement {
      connectedCallback() {
        this.frame = this.querySelector('[data-xray-frame]');
        this.marker = this.querySelector('[data-xray-marker]');
        if (!this.frame) return;

        this.cur = { x: 0.5, y: 0.5, r: 0 };
        this.target = { x: 0.5, y: 0.5, r: 0 };
        this.lensRaf = 0;
        this.introRaf = 0;
        this.scrollRaf = 0;
        this.stopMode = null;

        this.resizer = new ResizeObserver(() => this.sizeBleed());
        this.resizer.observe(this.frame);

        this.onModeChange = () => this.setMode();
        LENS_QUERY.addEventListener('change', this.onModeChange);
        this.setMode();
      }

      disconnectedCallback() {
        this.resizer?.disconnect();
        LENS_QUERY.removeEventListener('change', this.onModeChange);
        this.stopMode?.();
        this.stopMode = null;
      }

      /** Layer bleed in px; images pad it back off so art sits on the frame. */
      sizeBleed() {
        this.style.setProperty('--xray-bleed', (BLEED * this.frame.clientWidth).toFixed(1) + 'px');
      }

      /** Tear down the current mode and start the one the device wants now. */
      setMode() {
        this.stopMode?.();
        this.lens = LENS_QUERY.matches;
        if (this.lens) this.stopMode = this.startLens();
        else if (!NATIVE_SCROLL) this.stopMode = this.startScrollFallback();
        else this.stopMode = () => {};
      }

      /** Inactive showcase slides are visibility:hidden but still intersect. */
      isShown() {
        return typeof this.checkVisibility !== 'function' || this.checkVisibility({ visibilityProperty: true });
      }

      /* ---------- lens mode ---------- */

      /** Bind pointer / keyboard and the first-view intro. Returns its teardown. */
      startLens() {
        const onMove = (event) => {
          if (event.pointerType !== 'mouse') return;
          this.stopIntro();
          this.openAt(this.pointAt(event));
        };
        const onLeave = (event) => {
          if (event.pointerType === 'mouse') this.close();
        };
        const onFocus = () => {
          if (!this.frame.matches(':focus-visible')) return;
          this.stopIntro();
          this.openAt({ x: 0.5, y: 0.5 });
        };
        const onBlur = () => this.close();
        const onKey = (event) => this.onKeyDown(event);

        this.frame.addEventListener('pointermove', onMove);
        this.frame.addEventListener('pointerleave', onLeave);
        this.frame.addEventListener('focus', onFocus);
        this.frame.addEventListener('blur', onBlur);
        this.frame.addEventListener('keydown', onKey);

        const observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting) && this.isShown()) {
              observer.disconnect();
              this.playIntro();
            }
          },
          { threshold: 0.5 }
        );
        observer.observe(this);

        return () => {
          this.frame.removeEventListener('pointermove', onMove);
          this.frame.removeEventListener('pointerleave', onLeave);
          this.frame.removeEventListener('focus', onFocus);
          this.frame.removeEventListener('blur', onBlur);
          this.frame.removeEventListener('keydown', onKey);
          observer.disconnect();
          this.stopIntro();
          cancelAnimationFrame(this.lensRaf);
          this.lensRaf = 0;
          this.cur = { x: 0.5, y: 0.5, r: 0 };
          this.target = { ...this.cur };
          this.paintLens();
        };
      }

      /** Pointer position as frame fractions (clamped to the frame). */
      pointAt(event) {
        const rect = this.frame.getBoundingClientRect();
        return {
          x: clamp01((event.clientX - rect.left) / rect.width),
          y: clamp01((event.clientY - rect.top) / rect.height),
        };
      }

      /** Open (or move) the lens at p. A closed lens grows in place rather than sliding over. */
      openAt(p) {
        if (this.cur.r < 0.01) {
          this.cur.x = p.x;
          this.cur.y = p.y;
        }
        this.target = { x: p.x, y: p.y, r: LENS };
        this.runLens();
      }

      close() {
        this.target.r = 0;
        this.runLens();
      }

      onKeyDown(event) {
        const moves = { ArrowLeft: [-STEP, 0], ArrowRight: [STEP, 0], ArrowUp: [0, -STEP], ArrowDown: [0, STEP] };
        if (event.key === 'Escape') {
          this.close();
          return;
        }
        const move = moves[event.key];
        if (!move) return;
        event.preventDefault();
        this.stopIntro();
        this.openAt({ x: clamp01(this.target.x + move[0]), y: clamp01(this.target.y + move[1]) });
      }

      /**
       * Short wander across the bag, then close. Lens mode only (touch is
       * scroll-driven); skipped for reduced motion or while the lens is in use.
       * Public: daddl-claims calls it when a slide is picked.
       */
      playIntro() {
        if (!this.lens || REDUCED_QUERY.matches || this.target.r > 0) return;
        this.stopIntro();

        const path = [
          { at: 0, x: 0.38, y: 0.42, r: 0 },
          { at: 0.18, x: 0.38, y: 0.42, r: LENS },
          { at: 0.5, x: 0.62, y: 0.4, r: LENS },
          { at: 0.8, x: 0.52, y: 0.62, r: LENS },
          { at: 1, x: 0.52, y: 0.62, r: 0 },
        ];
        const duration = 2600;
        const start = performance.now();

        const step = (now) => {
          const t = Math.min(1, (now - start) / duration);
          let i = 1;
          while (i < path.length - 1 && t > path[i].at) i += 1;
          const a = path[i - 1];
          const b = path[i];
          const e = smooth((t - a.at) / (b.at - a.at || 1));
          this.cur = { x: a.x + (b.x - a.x) * e, y: a.y + (b.y - a.y) * e, r: a.r + (b.r - a.r) * e };
          this.target = { ...this.cur };
          this.paintLens();
          this.introRaf = t < 1 ? requestAnimationFrame(step) : 0;
        };
        this.introRaf = requestAnimationFrame(step);
      }

      stopIntro() {
        if (!this.introRaf) return;
        cancelAnimationFrame(this.introRaf);
        this.introRaf = 0;
      }

      runLens() {
        if (!this.lensRaf) this.lensRaf = requestAnimationFrame(() => this.tickLens());
      }

      /** Ease cur → target; stop once settled. */
      tickLens() {
        this.lensRaf = 0;
        const k = REDUCED_QUERY.matches ? 1 : EASE;
        let settled = true;
        ['x', 'y', 'r'].forEach((key) => {
          const diff = this.target[key] - this.cur[key];
          if (Math.abs(diff) > 0.001) {
            this.cur[key] += diff * k;
            settled = false;
          } else {
            this.cur[key] = this.target[key];
          }
        });
        this.paintLens();
        if (!settled) this.runLens();
      }

      /** Write the lens to CSS (px, frame-relative). */
      paintLens() {
        const width = this.frame.clientWidth;
        const height = this.frame.clientHeight;
        this.style.setProperty('--xray-x', (this.cur.x * width).toFixed(1) + 'px');
        this.style.setProperty('--xray-y', (this.cur.y * height).toFixed(1) + 'px');
        this.style.setProperty('--xray-r', (this.cur.r * width).toFixed(1) + 'px');
        this.classList.toggle('is-open', this.cur.r > 0.02);
      }

      /* ---------- scroll mode fallback (no CSS scroll timelines) ---------- */

      /** Passive scroll listener, attached only while the bag is on screen. Returns its teardown. */
      startScrollFallback() {
        if (!this.marker) return () => {};

        const onScroll = () => {
          if (!this.scrollRaf) this.scrollRaf = requestAnimationFrame(() => this.paintScroll());
        };
        const listen = (on) => {
          const method = on ? 'addEventListener' : 'removeEventListener';
          window[method]('scroll', onScroll, { passive: true });
          window[method]('resize', onScroll, { passive: true });
        };

        const observer = new IntersectionObserver((entries) => {
          const visible = entries.some((entry) => entry.isIntersecting);
          listen(visible);
          if (visible) onScroll();
        });
        observer.observe(this);

        return () => {
          observer.disconnect();
          listen(false);
          cancelAnimationFrame(this.scrollRaf);
          this.scrollRaf = 0;
          this.style.removeProperty('--xray-p');
        };
      }

      /**
       * --xray-p 0 → 1 as the marker travels SCROLL_FROM → SCROLL_TO of the
       * viewport, linear like the CSS. One rect read + one property write
       * per frame. Hidden
       * showcase slides keep updating, so a bag picked mid-scroll matches.
       */
      paintScroll() {
        this.scrollRaf = 0;
        const vh = window.innerHeight || document.documentElement.clientHeight;
        const y = this.marker.getBoundingClientRect().top;
        const p = clamp01((vh * SCROLL_FROM - y) / (vh * (SCROLL_FROM - SCROLL_TO)));
        this.style.setProperty('--xray-p', p.toFixed(4));
      }
    }
  );
}
